import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Grid, CircularProgress, Table, TableHead, TableRow,
  TableCell, TableBody, Typography, Alert, Chip, Tabs, Tab,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useHistory, useParams, useRouteMatch, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import ConfirmDialog from '../../components/ConfirmDialog';
import RfqQuoteComparisonTable from '../../components/material/RfqQuoteComparisonTable';
import RfqAwardDialog from '../../components/material/RfqAwardDialog';
import RfqQuoteEntryDialog from '../../components/material/RfqQuoteEntryDialog';
import RfqVendorsPanel from '../../components/material/RfqVendorsPanel';
import RfqSplitAwardDialog from '../../components/material/RfqSplitAwardDialog';
import RfqUnsealDialog from '../../components/material/RfqUnsealDialog';
import RfqPortalLinkCard from '../../components/material/RfqPortalLinkCard';
import RfqClarificationsPanel from '../../components/material/RfqClarificationsPanel';
import { DetailRow } from '../../components/InvField';
import { writePortalItemCache } from '../../../vendor-portal/preview';
import { resolveItemGst } from '../../utils/itemDisplay';
import {
  getRfq, getRfqItems, getRfqQuotes,
  getRfqQuoteComparison, getAcceptedQuotes,
  publishRfq, closeRfq, cancelRfq,
  persistPortalItemSnapshots,
} from '../../api/rfq';
import { notifyInvitedVendorsOnPublish, summarizeRfqInviteNotify } from '../../utils/rfqInviteNotify';
import { getPurchasableVendors, getVendor } from '../../api/vendor';
import { getPurchaseOrders, getPurchaseOrder, createPoFromAcceptedQuote } from '../../api/procurement';
import { getUoms } from '../../api/inventoryMaster';
import useItemNames from '../../hooks/useItemNames';
import {
  fromApiRfq, fromApiRfqItem, getRfqWorkflowIndex, isRfqPublished,
  normalizeApiList, canAcceptQuote, collectRfqVendorIds,
  invitationVendorId, resolveRfqVendorLabel, staffQuoteChip, quoteLifecycleStatus,
  purchaseOrderId, collectAwardPurchaseOrders, toPoFromQuoteBody,
  invitationsFromInvitedVendors,
} from '../../utils/rfqEntity';
import { normalizeUomList } from '../../utils/masterEntity';
import { hasDisplayValue } from '../../utils/poLifecycle';
import { RFQ_WORKFLOW_STAGES } from '../../constants/rfqManagement';
import dayjs from 'dayjs';
import { formatDate, formatDateTime, formatCurrency } from '../../utils';
import { SEALED_PLACEHOLDER, rankQuotes, lowestComparableNet, quoteAllInNet } from '../../utils/rfqComparison';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { IPD_COLORS } from '../../theme';

function uniqueById(list, pickId) {
  const seen = new Set();
  return list.filter((row) => {
    const id = pickId(row);
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

function buildUomMap(uoms) {
  const map = {};
  (Array.isArray(uoms) ? uoms : []).forEach((u) => {
    const id = u.uom_id || u.id;
    if (!id) return;
    map[id] = u.uom_name || u.uom_code || u.name || '';
  });
  return map;
}

function resolveUomLabel(item, uomMap = {}) {
  if (hasDisplayValue(item?.uom_name)) return item.uom_name;
  if (hasDisplayValue(item?.uom_code) && !/^[0-9a-f-]{36}$/i.test(item.uom_code)) {
    return item.uom_code;
  }
  if (item?.uom_id && uomMap[item.uom_id]) return uomMap[item.uom_id];
  return '—';
}

function RfqAwardedSection({
  rows, pos = [], isSplit, awardedAt, onOpenPo, onCreatePos, creatingPo,
  onCreateWo,
}) {
  if (!rows.length && !pos.length) {
    return (
      <SectionCard title="Awarded" sx={{ mb: 2.5 }}>
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
          This RFQ is awarded. Winning quote details are not on the quote list yet.
        </Typography>
      </SectionCard>
    );
  }

  return (
    <SectionCard title="Awarded" sx={{ mb: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        <StatusChip status="AWARDED" label="Awarded" />
        {isSplit && (
          <Chip
            size="small"
            label="Split award"
            sx={{ height: 22, fontSize: 11, backgroundColor: IPD_COLORS.selectedBg }}
          />
        )}
        {awardedAt && (
          <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
            {formatDateTime(awardedAt)}
          </Typography>
        )}
      </Box>
      {rows.length > 0 && (
      <Box sx={{ overflowX: 'auto' }}>
        <Table
          size="small"
          sx={{
            minWidth: 480,
            '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary, py: 1.25 },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Vendor</TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Quote #</TableCell>
              <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Awarded value</TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Remarks</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell sx={{ fontWeight: 600 }}>{row.vendorLabel}</TableCell>
                <TableCell>{row.quoteNumber}</TableCell>
                <TableCell align="right">{row.amount != null ? formatCurrency(row.amount) : '—'}</TableCell>
                <TableCell sx={{ color: IPD_COLORS.textSecondary, whiteSpace: 'normal' }}>
                  {row.remarks || '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      )}
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: IPD_COLORS.textPrimary, mt: rows.length ? 2 : 0, mb: 1 }}>
        Purchase orders ({pos.length})
      </Typography>
      {pos.length === 0 ? (
        <Box>
          <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
            No purchase order is linked to this award yet.
          </Typography>
          {onCreatePos && (
            <Box sx={{ mt: 1.5 }}>
              <InvButton size="small" tone="mint" onClick={onCreatePos} disabled={creatingPo}>
                {creatingPo ? 'Creating…' : 'Create purchase order'}
              </InvButton>
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table
            size="small"
            sx={{
              minWidth: 520,
              '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary, py: 1.25 },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: IPD_COLORS.textSecondary }}>PO number</TableCell>
                <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Vendor</TableCell>
                <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Value</TableCell>
                <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {pos.map((po) => {
                const poId = purchaseOrderId(po);
                const vendorLabel = po.vendor_name || po.vendorLabel || '—';
                const value = po.grand_total ?? po.total_amount ?? po.total_net_amount
                  ?? po.net_amount ?? po.total_value ?? po.total_basic_amount;
                return (
                  <TableRow key={poId}>
                    <TableCell sx={{ fontWeight: 600 }}>{po.po_number || po.purchase_order_number || poId}</TableCell>
                    <TableCell>{vendorLabel}</TableCell>
                    <TableCell align="right">{value != null ? formatCurrency(value) : '—'}</TableCell>
                    <TableCell>
                      <StatusChip status={po.status || po.approval_status} />
                    </TableCell>
                    <TableCell>
                      {onOpenPo && poId && (
                        <InvButton size="small" tone="teal" variant="outlined" onClick={() => onOpenPo(poId)}>
                          Open
                        </InvButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
      {onCreateWo && (
        <Box sx={{ mt: 1.5 }}>
          <InvButton size="small" variant="outlined" tone="teal" onClick={onCreateWo}>
            Create Work Order
          </InvButton>
        </Box>
      )}
      {isSplit && (
        <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, mt: 1.5 }}>
          Split award — one purchase order per winning vendor.
        </Typography>
      )}
    </SectionCard>
  );
}

/**
 * The at-a-glance strip above the tabs.
 *
 * "Declined" is deliberately absent: the service has no decline field, so the
 * number would be a guess. Awaiting covers both "not looked yet" and "declined".
 */
function RfqSummaryStrip({ invitedCount, linkCount = 0, respondedCount, minimumQuotes, lowestNet, sealed }) {
  const awaiting = Math.max(invitedCount + linkCount - respondedCount, 0);
  const belowMinimum = minimumQuotes > 0 && respondedCount < minimumQuotes;

  const stats = [
    { label: 'Invited', value: invitedCount },
    ...(linkCount > 0 ? [{ label: 'Via public link', value: linkCount }] : []),
    {
      label: 'Responded',
      value: respondedCount,
      tone: belowMinimum ? IPD_COLORS.error.text : undefined,
      sub: minimumQuotes > 0 ? `minimum ${minimumQuotes}` : null,
    },
    { label: 'Awaiting', value: awaiting },
    {
      label: 'Lowest quote',
      value: sealed ? SEALED_PLACEHOLDER : (lowestNet == null ? '—' : formatCurrency(lowestNet)),
      wide: true,
    },
  ];

  return (
    <Box sx={{
      display: 'flex', flexWrap: 'wrap', gap: 0, mb: 2.5,
      border: `1px solid ${IPD_COLORS.border}`, borderRadius: '12px',
      backgroundColor: IPD_COLORS.cardBg, overflow: 'hidden',
    }}>
      {stats.map((stat, i) => (
        <Box
          key={stat.label}
          sx={{
            flex: stat.wide ? '2 1 200px' : '1 1 120px',
            px: 2, py: 1.5,
            borderLeft: i === 0 ? 'none' : `1px solid ${IPD_COLORS.border}`,
          }}
        >
          <Typography sx={{
            fontSize: 11, fontWeight: 600, letterSpacing: '.06em',
            textTransform: 'uppercase', color: IPD_COLORS.textSecondary,
          }}>
            {stat.label}
          </Typography>
          <Typography sx={{
            fontSize: 22, fontWeight: 700,
            color: stat.tone || IPD_COLORS.textPrimary, lineHeight: 1.2,
          }}>
            {stat.value}
          </Typography>
          {stat.sub && (
            <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary }}>
              {stat.sub}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}

function RfqWorkflowBar({ status }) {
  const currentIndex = getRfqWorkflowIndex(status);
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
      mb: 2.5, p: { xs: 1.5, sm: 2 }, borderRadius: '12px',
      border: `1px solid ${IPD_COLORS.border}`, backgroundColor: IPD_COLORS.cardBg,
    }}>
      {RFQ_WORKFLOW_STAGES.map((stage, i) => {
        const isCompleted = currentIndex > i;
        const isActive = currentIndex === i || (status === 'EVALUATED' && stage.key === 'CLOSED');
        return (
          <React.Fragment key={stage.key}>
            <Chip label={stage.label} size="small"
              sx={{
                fontWeight: 700,
                backgroundColor: isCompleted || isActive ? IPD_COLORS.primary : IPD_COLORS.selectedBg,
                color: isCompleted || isActive ? '#fff' : IPD_COLORS.textSecondary,
              }} />
            {i < RFQ_WORKFLOW_STAGES.length - 1 && (
              <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 12, display: { xs: 'none', sm: 'inline' } }}>→</Typography>
            )}
          </React.Fragment>
        );
      })}
      <Box sx={{ flex: { xs: '1 1 100%', sm: 1 }, display: { xs: 'none', sm: 'block' } }} />
      <StatusChip status={status === 'OPEN' ? 'PUBLISHED' : status} />
    </Box>
  );
}

const TAB_OVERVIEW = 'overview';
const TAB_VENDORS = 'vendors';
const TAB_QUOTES = 'quotes';
const TAB_COMPARISON = 'comparison';

/** ConfirmDialog action keys on this screen -> the permission each one needs. */
const DIALOG_ACTION_PERMISSION = {
  publish: INV_ACTIONS.RFQ_PUBLISH,
  close: INV_ACTIONS.RFQ_CLOSE,
  cancel: INV_ACTIONS.RFQ_CANCEL,
};

export default function RfqDetail() {
  const { id } = useParams();
  const { url } = useRouteMatch();
  const history = useHistory();
  const { can } = useInvRole();
  const location = useLocation();
  const { resolveItem, isControlled, catalog } = useItemNames();

  const [rfq, setRfq] = useState(null);
  const [rfqItems, setRfqItems] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [vendorDirectory, setVendorDirectory] = useState([]);
  const [uomMap, setUomMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [actioning, setActioning] = useState(false);
  const [activeTab, setActiveTab] = useState(TAB_OVERVIEW);

  const [comparison, setComparison] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonError, setComparisonError] = useState(null);

  const [awardTarget, setAwardTarget] = useState(null);
  const [quoteEntryOpen, setQuoteEntryOpen] = useState(false);
  const [splitAwardOpen, setSplitAwardOpen] = useState(false);
  const [unsealOpen, setUnsealOpen] = useState(false);
  const [emailingVendors, setEmailingVendors] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [acceptedQuotes, setAcceptedQuotes] = useState([]);
  const [awardPos, setAwardPos] = useState([]);
  const [creatingPo, setCreatingPo] = useState(false);

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const justSaved = query.get('saved') === '1';
  const justPublished = query.get('published') === '1';
  const editUrl = `${url}/edit`;
  const poBaseUrl = url.replace(/\/rfqs\/[^/]+$/, '/po');

  const loadCore = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // The summary endpoint is no longer fetched: the strip derives the same
      // counts from the quotes and invited list already loaded here.
      const [r, items, q, vendors, uoms, accepted] = await Promise.all([
        getRfq(id),
        getRfqItems(id).catch(() => []),
        getRfqQuotes(id).catch(() => []),
        getPurchasableVendors().catch(() => []),
        getUoms({ is_active: true, limit: 200 }).catch(() => []),
        getAcceptedQuotes({ rfq_id: id }).catch(() => []),
      ]);
      const rfqRow = fromApiRfq(r);
      const quoteList = normalizeApiList(q);
      const invitationList = invitationsFromInvitedVendors(rfqRow);
      const knownIds = new Set((vendors || []).map((v) => v.vendor_id || v.id));
      const missingIds = collectRfqVendorIds(rfqRow, invitationList, quoteList)
        .filter((vid) => vid && !knownIds.has(vid));
      const extras = (await Promise.all(missingIds.map((vid) => getVendor(vid).catch(() => null))))
        .filter(Boolean);

      setRfq(rfqRow);
      setRfqItems(normalizeApiList(items).map(fromApiRfqItem));
      setQuotes(quoteList);
      setVendorDirectory([...(vendors || []), ...extras]);
      setUomMap(buildUomMap(normalizeUomList(uoms)));
      setInvitations(invitationList);
      const quoteIds = new Set(quoteList.map((row) => row.id || row.vendor_quote_id).filter(Boolean));
      const acceptedFromRfq = normalizeApiList(accepted);
      const acceptedQuoteRows = quoteList.filter((row) => quoteLifecycleStatus(row) === 'ACCEPTED');
      const extraAccepted = await Promise.all(
        (acceptedQuoteRows.length ? acceptedQuoteRows : quoteList).map((row) => getAcceptedQuotes({
          vendor_quote_id: row.id || row.vendor_quote_id,
        }).catch(() => [])),
      );
      const acceptedAll = await getAcceptedQuotes().catch(() => []);
      const matchedAccepted = uniqueById([
        ...acceptedFromRfq,
        ...extraAccepted.flatMap((chunk) => normalizeApiList(chunk)),
        ...normalizeApiList(acceptedAll),
      ], (row) => row.id || row.accepted_quote_id || row.vendor_quote_id).filter((row) => (
        row.rfq_id === id || row.rfq === id
        || quoteIds.has(row.vendor_quote_id || row.quote_id || row.vendor_quote)
      ));
      setAcceptedQuotes(matchedAccepted);

      const seedQuotes = acceptedQuoteRows.length
        ? acceptedQuoteRows
        : (String(rfqRow.status || '').toUpperCase() === 'AWARDED' ? quoteList : []);
      const awardedVendorIds = seedQuotes.map((row) => row.vendor_id || row.vendorId).filter(Boolean);
      const awardedAmounts = seedQuotes
        .map((row) => Number(row.total_net_amount ?? row.total_value))
        .filter((n) => Number.isFinite(n) && n > 0);
      const poList = normalizeApiList(await getPurchaseOrders().catch(() => []));
      const poIdFromAccepted = matchedAccepted
        .map((row) => row.purchase_order_id || row.po_id || row.converted_po_id)
        .filter(Boolean);
      const poById = (await Promise.all(poIdFromAccepted.map((pid) => getPurchaseOrder(pid).catch(() => null))))
        .filter(Boolean);
      setAwardPos(collectAwardPurchaseOrders({
        purchaseOrders: [...poList, ...poById],
        acceptedQuotes: matchedAccepted,
        awardedVendorIds: awardedVendorIds.length
          ? awardedVendorIds
          : matchedAccepted.map((row) => row.vendor_id).filter(Boolean),
        awardedQuoteIds: [...quoteIds],
        awardedAmounts: awardedAmounts.length
          ? awardedAmounts
          : matchedAccepted.map((row) => Number(row.accepted_value || row.total_net_amount)).filter((n) => n > 0),
        rfqId: id,
        rfqNumber: rfqRow.rfq_number,
        quoteNumbers: quoteList.map((row) => row.quote_number).filter(Boolean),
      }));
    } catch (e) {
      setError(e.message || 'Failed to load RFQ');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!id || !catalog || !rfqItems.length) return;
    let cancelled = false;
    (async () => {
      const named = await Promise.all(rfqItems.map(async (item) => {
        const label = catalog.resolve(item);
        const master = catalog.byId?.get(item.item_id);
        const gst = await resolveItemGst(item.item_id, {
          ...master,
          ...item,
          hsn_code: item.hsn || master?.hsn_code || master?.hsn,
        });
        return {
          ...item,
          item_name: label && label !== '—' ? label : item.item_name,
          hsn: item.hsn || master?.hsn_code || master?.hsn || '',
          uom_code: item.uom_code
            || catalog.getPurchaseUomCode?.(item.item_id)
            || catalog.getBaseUomCode?.(item.item_id)
            || master?.purchase_uom_code
            || master?.base_uom_code
            || '',
          gst_rate: gst || item.gst_rate || catalog.getGstRate?.(item.item_id) || master?.gst_rate || '',
        };
      }));
      if (cancelled) return;
      persistPortalItemSnapshots(id, named).catch(() => {});
      writePortalItemCache(id, rfq?.rfq_number, named);
    })();
    return () => { cancelled = true; };
  }, [id, catalog, rfqItems, rfq?.rfq_number]);

  const loadComparison = useCallback(async () => {
    setComparisonLoading(true);
    setComparisonError(null);
    try {
      const data = await getRfqQuoteComparison(id);
      setComparison(data);
    } catch (e) {
      setComparisonError(e.message || 'Failed to load comparison');
    } finally {
      setComparisonLoading(false);
    }
  }, [id]);

  useEffect(() => { loadCore(); }, [loadCore]);

  // Ranking drives award guards, not just the Comparison tab — so it has to
  // be loaded before anyone opens that tab.
  useEffect(() => {
    if (quotes.length > 0 && !comparison && !comparisonLoading && !comparisonError) {
      loadComparison();
    }
  }, [quotes.length, comparison, comparisonLoading, comparisonError, loadComparison]);

  /**
   * The RFQ item API carries no drug flag, so it comes from the item catalog.
   * This is the controlled/narcotic flag specifically — a plain pharma item
   * that still needs a licensed vendor will not be caught by it.
   */
  const awardItems = useMemo(
    () => rfqItems.map((it) => ({ ...it, is_controlled_drug: isControlled(it.item_id) })),
    [rfqItems, isControlled],
  );

  const invitedVendors = useMemo(() => {
    const ids = collectRfqVendorIds(rfq, invitations, quotes);
    return ids.map((vid) => {
      const match = vendorDirectory.find((v) => (v.vendor_id || v.id) === vid);
      const invitation = invitations.find((i) => invitationVendorId(i) === vid);
      const invited = (rfq?.invited_vendor_ids || []).includes(vid);
      return {
        id: vid,
        label: resolveRfqVendorLabel(vid, { vendor: match, invitation }),
        invited,
      };
    });
  }, [rfq, vendorDirectory, invitations, quotes]);
  const invitedCount = (rfq?.invited_vendor_ids || []).length;
  const linkCount = Math.max(invitedVendors.length - invitedCount, 0);

  async function executeAction() {
    if (!dialog) return;
    const required = DIALOG_ACTION_PERMISSION[dialog.action];
    if (required && !can(required)) return;
    setActioning(true);
    try {
      if (dialog.action === 'publish') {
        await publishRfq(id);
        const notify = await notifyInvitedVendorsOnPublish(id, {
          rfq,
          vendorIds: rfq?.invited_vendor_ids,
        }).catch((err) => ({ sent: [], failed: [{ reason: err.message }] }));
        toast.success(`RFQ published. ${summarizeRfqInviteNotify(notify)}`);
      } else if (dialog.action === 'close') {
        await closeRfq(id);
        toast.success('RFQ closed for new quotes.');
      } else if (dialog.action === 'cancel') {
        await cancelRfq(id);
        toast.success('RFQ cancelled.');
      }
      setDialog(null);
      if (justSaved || justPublished) history.replace(url);
      await loadCore();
    } catch (e) {
      toast.error(e.message || 'Action failed');
      setDialog(null);
    } finally {
      setActioning(false);
    }
  }

  async function handleEmailVendors() {
    setEmailingVendors(true);
    try {
      const notify = await notifyInvitedVendorsOnPublish(id, {
        rfq,
        vendorIds: collectRfqVendorIds(rfq, invitations, quotes),
      });
      const summary = summarizeRfqInviteNotify(notify);
      const sentN = Array.isArray(notify.sent) ? notify.sent.length : (notify.sent || 0);
      const failedN = Array.isArray(notify.failed) ? notify.failed.length : (notify.failed || 0);
      if (sentN > 0 && !failedN) toast.success(summary);
      else toast.warn(summary);
    } catch (e) {
      toast.error(e.message || 'Could not email invited vendors');
    } finally {
      setEmailingVendors(false);
    }
  }

  function handleAwardSuccess({ poId }) {
    toast.success(poId ? 'Quote accepted and PO created.' : 'Quote accepted. RFQ awarded.');
    setAwardTarget(null);
    if (poId) {
      history.push(`${poBaseUrl}/${poId}`);
    } else {
      loadCore();
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress sx={{ color: IPD_COLORS.primary }} />
      </Box>
    );
  }
  if (error) return <Box sx={{ p: 4 }}><Alert severity="error">{error}</Alert></Box>;
  if (!rfq) return null;

  const status = rfq.status || 'DRAFT';
  const published = isRfqPublished(status);
  const minimumQuotes = Number(rfq.minimum_required_quotes) || 0;
  const belowMinimum = minimumQuotes > 0 && quotes.length < minimumQuotes
    && ['PUBLISHED', 'OPEN', 'CLOSED'].includes(status);
  const isOverdue = Boolean(rfq.submission_deadline)
    && dayjs().isAfter(dayjs(rfq.submission_deadline))
    && ['PUBLISHED', 'OPEN'].includes(status);
  const lowestNet = lowestComparableNet(rankQuotes(comparison));
  const showQuotesTab = published || ['CLOSED', 'EVALUATED', 'AWARDED'].includes(status);
  const showComparisonTab = showQuotesTab && quotes.length > 0;
  const canAward = can(INV_ACTIONS.RFQ_AWARD) && status !== 'AWARDED';

  const awardedByQuoteId = new Map();
  const addAwardedRow = (quote, extra = {}) => {
    const qid = quote.id || quote.vendor_quote_id || extra.vendor_quote_id || extra.quote_id;
    if (!qid || awardedByQuoteId.has(qid)) return;
    const vid = quote.vendor_id || quote.vendorId || extra.vendor_id;
    const vendor = vendorDirectory.find((v) => (v.vendor_id || v.id) === vid);
    const invitation = invitations.find((invRow) => invitationVendorId(invRow) === vid);
    awardedByQuoteId.set(qid, {
      id: qid,
      vendorLabel: resolveRfqVendorLabel(vid, { vendor, invitation, quote }),
              quoteNumber: quote.quote_number || extra.quote_number || extra.acceptance_number || '—',
              amount: quote.total_net_amount ?? quote.total_value ?? extra.accepted_value ?? extra.total_net_amount,
              remarks: extra.remarks || quote.remarks || '',
              awardedAt: extra.accepted_at || extra.created_at || quote.accepted_at,
              vendorId: vid,
              acceptedQuoteId: extra.id || extra.accepted_quote_id || quote.accepted_quote_id,
    });
  };
  quotes.filter((q) => quoteLifecycleStatus(q) === 'ACCEPTED').forEach((q) => addAwardedRow(q));
  acceptedQuotes.forEach((rec) => {
    const qid = rec.vendor_quote_id || rec.quote_id || rec.vendor_quote;
    const quote = quotes.find((q) => (q.id || q.vendor_quote_id) === qid) || rec;
    addAwardedRow(quote, rec);
  });
  const awardedRows = [...awardedByQuoteId.values()];
  const displayPos = awardPos.map((po) => {
    const vid = po.vendor_id || po.vendorId;
    if (po.vendor_name || !vid) return po;
    const vendor = vendorDirectory.find((v) => (v.vendor_id || v.id) === vid);
    const invitation = invitations.find((invRow) => invitationVendorId(invRow) === vid);
    return { ...po, vendorLabel: resolveRfqVendorLabel(vid, { vendor, invitation }) };
  });
  const showAwardedSection = status === 'AWARDED' || awardedRows.length > 0 || displayPos.length > 0;

  async function handleCreateAwardPos() {
    const ids = [...new Set([
      ...awardedRows.map((row) => row.acceptedQuoteId),
      ...acceptedQuotes.map((row) => row.id || row.accepted_quote_id),
    ].filter(Boolean))];
    if (!ids.length) {
      toast.error('Accepted quote is missing, so a purchase order cannot be created from here.');
      return;
    }
    setCreatingPo(true);
    try {
      for (let i = 0; i < ids.length; i += 1) {
        await createPoFromAcceptedQuote(toPoFromQuoteBody(ids[i], {}));
      }
      toast.success(ids.length === 1 ? 'Purchase order created.' : `${ids.length} purchase orders created.`);
      await loadCore();
    } catch (e) {
      toast.error(e.message || 'Failed to create purchase order');
    } finally {
      setCreatingPo(false);
    }
  }

  return (
    <Box>
      <PageHeader
        eyebrow="Material Management › RFQs"
        title={rfq.rfq_number}
        description={rfq.rfq_title || rfq.title}
        actions={(
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <InvButton variant="outlined" tone="teal" size="small" onClick={() => history.goBack()}>
              Back
            </InvButton>
            {status === 'DRAFT' && (
              <>
                {can(INV_ACTIONS.RFQ_EDIT) && (
                  <InvButton size="small" variant="outlined" tone="teal" startIcon={<EditIcon />}
                    onClick={() => history.push(editUrl)}>
                    Edit
                  </InvButton>
                )}
                {can(INV_ACTIONS.RFQ_PUBLISH) && (
                  <InvButton size="small" tone="mint"
                    onClick={() => setDialog({
                      action: 'publish',
                      title: 'Publish RFQ',
                      desc: `Publish "${rfq.rfq_number}" and notify ${invitedVendors.length} invited vendor(s)?`,
                    })}>
                    Publish RFQ
                  </InvButton>
                )}
              </>
            )}
            {published && (
              <>
                {can(INV_ACTIONS.RFQ_PUBLISH) && (
                  <InvButton
                    size="small"
                    variant="outlined"
                    tone="teal"
                    loading={emailingVendors}
                    onClick={handleEmailVendors}
                  >
                    Email vendors
                  </InvButton>
                )}
                {can(INV_ACTIONS.RFQ_CLOSE) && (
                  <InvButton size="small" tone="amber" variant="outlined"
                    onClick={() => setDialog({ action: 'close', title: 'Close RFQ', desc: 'Close this RFQ for new quote submissions?' })}>
                    Close
                  </InvButton>
                )}
                {can(INV_ACTIONS.RFQ_CANCEL) && (
                  <InvButton size="small" tone="red" variant="outlined"
                    onClick={() => setDialog({ action: 'cancel', title: 'Cancel RFQ', desc: 'Cancel this RFQ entirely?', destructive: true })}>
                    Cancel
                  </InvButton>
                )}
              </>
            )}
          </Box>
        )}
      />

      {justSaved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          RFQ saved as <strong>Draft</strong>. Review details, then <strong>Publish RFQ</strong> to invite vendors.
        </Alert>
      )}
      {justPublished && (
        <Alert severity="success" sx={{ mb: 2 }}>
          RFQ published successfully. Invited vendors can submit quotes.
        </Alert>
      )}

      <RfqWorkflowBar status={status} />

      {isOverdue && (
        <Alert severity="error" sx={{ mb: 2 }}>
          The submission deadline passed on {formatDateTime(rfq.submission_deadline)} but this
          RFQ is still open. Close it, or extend the deadline, so vendors know where they stand.
        </Alert>
      )}

      {belowMinimum && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {quotes.length} of {minimumQuotes} required quotes received. Awarding below the
          minimum will need a written justification.
        </Alert>
      )}

      <RfqSummaryStrip
        invitedCount={invitedCount}
        linkCount={linkCount}
        respondedCount={quotes.length}
        minimumQuotes={minimumQuotes}
        lowestNet={lowestNet}
        sealed={comparison?.rates_visible === false}
      />

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 2.5, borderBottom: `1px solid ${IPD_COLORS.border}` }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="Overview" value={TAB_OVERVIEW} />
        <Tab label={`Vendors (${invitedVendors.length})`} value={TAB_VENDORS} />
        {showQuotesTab && <Tab label={`Quotes (${quotes.length})`} value={TAB_QUOTES} />}
        {showComparisonTab && <Tab label="Comparison" value={TAB_COMPARISON} />}
      </Tabs>

      {activeTab === TAB_OVERVIEW && (
        <>
          {showAwardedSection && (
            <RfqAwardedSection
              rows={awardedRows}
              pos={displayPos}
              isSplit={awardedRows.length > 1}
              awardedAt={awardedRows[0]?.awardedAt}
              onOpenPo={can(INV_ACTIONS.PO_VIEW)
                ? (poId) => history.push(`${poBaseUrl}/${poId}`)
                : undefined}
              onCreatePos={can(INV_ACTIONS.PO_CREATE) && displayPos.length === 0
                ? handleCreateAwardPos
                : undefined}
              onCreateWo={can(INV_ACTIONS.RFQ_CREATE)
                ? () => {
                  const vendorId = awardedRows[0]?.vendorId || awardedRows[0]?.vendor_id || '';
                  const qs = new URLSearchParams({ rfq_id: id });
                  if (vendorId) qs.set('vendor_id', vendorId);
                  history.push(`${url.replace(/\/rfqs\/[^/]+$/, '/work-order/new')}?${qs.toString()}`);
                }
                : undefined}
              creatingPo={creatingPo}
            />
          )}
          <Grid container spacing={2.5} alignItems="flex-start">
          <Grid item xs={12} md={5}>
            <SectionCard title="RFQ Details" sx={{ height: '100%' }}>
              <DetailRow label="Number">{rfq.rfq_number}</DetailRow>
              {hasDisplayValue(rfq.rfq_title || rfq.title) && (
                <DetailRow label="Title">{rfq.rfq_title || rfq.title}</DetailRow>
              )}
              {hasDisplayValue(rfq.rfq_type) && (
                <DetailRow label="Type">{rfq.rfq_type}</DetailRow>
              )}
              {hasDisplayValue(rfq.issue_date) && (
                <DetailRow label="Issue Date">{formatDateTime(rfq.issue_date)}</DetailRow>
              )}
              {hasDisplayValue(rfq.submission_deadline || rfq.deadline) && (
                <DetailRow label="Submission Deadline">
                  {formatDateTime(rfq.submission_deadline || rfq.deadline)}
                </DetailRow>
              )}
              {(rfq.minimum_required_quotes != null || rfq.required_quotes_count != null) && (
                <DetailRow label="Min. Quotes">
                  {Number(rfq.minimum_required_quotes ?? rfq.required_quotes_count) === 0
                    ? 'No minimum'
                    : (rfq.minimum_required_quotes ?? rfq.required_quotes_count)}
                </DetailRow>
              )}
              {hasDisplayValue(rfq.department_name)
                || (hasDisplayValue(rfq.department_id) && !/^[0-9a-f-]{36}$/i.test(String(rfq.department_id))) ? (
                <DetailRow label="Department">
                  {rfq.department_name || rfq.department_id}
                </DetailRow>
              ) : null}
              {hasDisplayValue(rfq.terms_and_conditions) && (
                <DetailRow label="Terms & Conditions">{rfq.terms_and_conditions}</DetailRow>
              )}
              <DetailRow label="Open bid">
                {rfq.is_open_bid ? 'Yes — new vendors can register on the public link' : 'No — invited vendors only'}
              </DetailRow>
              <DetailRow label="Sealed bid">
                {rfq.is_sealed_bid ? 'Yes — rates hidden until the deadline' : 'No'}
              </DetailRow>
              {hasDisplayValue(rfq.notes) && (
                <DetailRow label="Notes">{rfq.notes}</DetailRow>
              )}
            </SectionCard>

            <SectionCard title="Vendors" sx={{ mt: 2.5 }}>
              {invitedVendors.length === 0 ? (
                <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
                  No vendors invited or registered on this RFQ yet.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {invitedVendors.map((v) => (
                    <Box
                      key={v.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{v.label}</Typography>
                      <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                        {v.invited ? 'Invited' : 'Registered via link'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={7}>
            <SectionCard title="Line Items" sx={{ height: '100%' }}>
              <Box sx={{ overflowX: 'auto' }}>
                <Table
                  size="small"
                  sx={{
                    minWidth: 560,
                    '& td, & th': {
                      borderColor: IPD_COLORS.border,
                      color: IPD_COLORS.textPrimary,
                      py: 1.25,
                      whiteSpace: 'nowrap',
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: IPD_COLORS.textSecondary, minWidth: 160 }}>Item</TableCell>
                      <TableCell sx={{ color: IPD_COLORS.textSecondary, minWidth: 140 }}>Specification</TableCell>
                      <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 88 }}>UOM</TableCell>
                      <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary, width: 72 }}>Qty</TableCell>
                      <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary, width: 110 }}>Last rate</TableCell>
                      <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 120 }}>Required By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rfqItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ textAlign: 'center', color: IPD_COLORS.textSecondary, py: 3 }}>
                          No items
                        </TableCell>
                      </TableRow>
                    ) : rfqItems.map((item, i) => (
                      <TableRow key={item.id || i}>
                        <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word', fontWeight: 600 }}>
                          {resolveItem(item)}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'normal', color: IPD_COLORS.textSecondary }}>
                          {hasDisplayValue(item.specification) ? item.specification : '—'}
                        </TableCell>
                        <TableCell>{resolveUomLabel(item, uomMap)}</TableCell>
                        <TableCell align="right">{item.required_quantity}</TableCell>
                        <TableCell align="right">
                          {item.last_rate != null ? formatCurrency(item.last_rate) : '—'}
                        </TableCell>
                        <TableCell>
                          {item.required_by_date ? formatDate(item.required_by_date) : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </SectionCard>
          </Grid>
        </Grid>
        </>
      )}

      {activeTab === TAB_VENDORS && (
        <SectionCard title="Invited Vendors">
          <RfqPortalLinkCard
            rfqId={id}
            rfq={rfq}
            items={rfqItems.map((item) => ({
              ...item,
              item_name: resolveItem(item) === '—' ? item.item_name : resolveItem(item),
              gst_rate: item.gst_rate || catalog?.getGstRate?.(item.item_id) || '',
            }))}
            published={published || ['CLOSED', 'EVALUATED', 'AWARDED'].includes(status)}
            canToggleOpenBid={can(INV_ACTIONS.RFQ_EDIT) || can(INV_ACTIONS.RFQ_PUBLISH)}
            onUpdated={loadCore}
          />
          <RfqVendorsPanel
            rfq={rfq}
            quotes={quotes}
            invitations={invitations}
            vendorDirectory={vendorDirectory}
            hasDrugItems={awardItems.some((it) => it.is_controlled_drug)}
            canEdit={can(INV_ACTIONS.RFQ_EDIT) && status === 'DRAFT'}
            onRefresh={(count) => {
              toast.success(`${count} vendor(s) invited.`);
              loadCore();
            }}
          />
          <RfqClarificationsPanel rfqId={id} />
        </SectionCard>
      )}

      {activeTab === TAB_QUOTES && showQuotesTab && (
        <SectionCard
          title="Vendor Quotes"
          actions={can(INV_ACTIONS.RFQ_EDIT) && rfqItems.length > 0 && (
            <InvButton size="small" tone="teal" onClick={() => setQuoteEntryOpen(true)}>
              Enter a quote
            </InvButton>
          )}
        >
          {quotes.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, py: 2 }}>
              No vendor quotes submitted yet. Use <strong>Enter a quote</strong> to record one
              received by email, letter or phone.
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table
                size="small"
                sx={{
                  minWidth: 640,
                  '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary, py: 1.25 },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Vendor</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Quote #</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Submitted</TableCell>
                    <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Total</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Status</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {quotes.map((q, i) => {
                    const vid = q.vendor_id || q.vendorId;
                    const vendor = vendorDirectory.find((v) => (v.vendor_id || v.id) === vid);
                    const invitation = invitations.find((invRow) => invitationVendorId(invRow) === vid);
                    const chip = staffQuoteChip(q.status);
                    return (
                    <TableRow key={q.id || q.vendor_quote_id || i}>
                      <TableCell>
                        {resolveRfqVendorLabel(vid, { vendor, invitation, quote: q })}
                      </TableCell>
                      <TableCell>{q.quote_number || '—'}</TableCell>
                      <TableCell>{formatDate(q.submitted_at)}</TableCell>
                      <TableCell align="right">
                        {(() => {
                          const net = quoteAllInNet(q, comparison);
                          return net == null ? SEALED_PLACEHOLDER : formatCurrency(net);
                        })()}
                      </TableCell>
                      <TableCell>
                        <StatusChip status={chip.status} label={chip.label} />
                      </TableCell>
                      <TableCell>
                        {canAcceptQuote(q) && canAward && (
                          <InvButton size="small" tone="mint" onClick={() => setAwardTarget(q)}>
                            Award
                          </InvButton>
                        )}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </SectionCard>
      )}

      {activeTab === TAB_COMPARISON && showComparisonTab && (
        <SectionCard title="Quote Comparison">
          <RfqQuoteComparisonTable
            comparison={comparison}
            loading={comparisonLoading}
            error={comparisonError}
            vendorDirectory={vendorDirectory}
            invitations={invitations}
            resolveItem={resolveItem}
            onUseSplit={canAward ? () => setSplitAwardOpen(true) : undefined}
            onAward={canAward ? (quoteId) => {
              const q = quotes.find((x) => (x.id || x.vendor_quote_id) === quoteId);
              if (q) setAwardTarget(q);
            } : undefined}
            onUnseal={can(INV_ACTIONS.RFQ_AWARD) ? () => setUnsealOpen(true) : undefined}
          />
        </SectionCard>
      )}

      <ConfirmDialog
        open={Boolean(dialog)}
        title={dialog?.title}
        description={dialog?.desc}
        destructive={dialog?.destructive}
        loading={actioning}
        onConfirm={executeAction}
        onCancel={() => setDialog(null)}
      />

      <RfqQuoteEntryDialog
        open={quoteEntryOpen}
        rfq={rfq}
        rfqItems={rfqItems}
        vendorDirectory={vendorDirectory}
        existingQuotes={quotes}
        resolveItem={resolveItem}
        onClose={() => setQuoteEntryOpen(false)}
        onSuccess={({ isPartial }) => {
          toast.success(isPartial
            ? 'Quote saved as a partial quote.'
            : 'Quote saved and submitted.');
          loadCore();
          loadComparison();
        }}
      />

      <RfqUnsealDialog
        open={unsealOpen}
        rfq={rfq}
        onClose={() => setUnsealOpen(false)}
        onSuccess={() => {
          toast.success('Rates unsealed. This has been recorded on the RFQ.');
          loadCore();
          loadComparison();
        }}
      />

      <RfqSplitAwardDialog
        open={splitAwardOpen}
        comparison={comparison}
        vendorDirectory={vendorDirectory}
        resolveItem={resolveItem}
        onClose={() => setSplitAwardOpen(false)}
        onSuccess={({ vendors }) => {
          toast.success(`Split award placed across ${vendors} vendor(s).`);
          loadCore();
          loadComparison();
        }}
      />

      <RfqAwardDialog
        open={Boolean(awardTarget)}
        quote={awardTarget}
        comparison={comparison}
        rfq={rfq}
        rfqItems={awardItems}
        vendorDirectory={vendorDirectory}
        onClose={() => setAwardTarget(null)}
        onSuccess={handleAwardSuccess}
      />
    </Box>
  );
}
